import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Download, CheckCircle, Circle } from "lucide-react"; //M10-15262
import { Autocomplete, Button, DatePicker, Input, Modal, Toggle } from "src/components";
import { RootState } from "src/context/store";
import { downloadInvoice, getInvoiceChangeValidationV1, getInvoiceDetails, getPoRefNoListing, getTextractData } from "src/services/invoice-api";
import { useToast } from "src/hooks/useToast";
import { editInvoice } from "src/services/invoice-api";
import { useDispatch } from "react-redux";
import { invoiceActions } from "src/context/invoice-slice";
import { counterActions } from "src/context/counter-slice";
import { useEventStatus } from "src/hooks/useEventStatus";
import { getOrderProductsForMapping } from "src/services/order-api";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import { get120DaysAgoDate } from "src/utils/utils";
import useDebounceLeading from "src/hooks/useDebounceLeading";
import usePermission from "src/hooks/usePermission";
import EscalateToMaster from "./InvoiceEntryCards/EscalateToMaster";
import SelectCounterModal from "./InvoiceEntryCards/SelectCounterModal";
import InvoiceReject from "./invoice-reject";
import { useNavigate } from "react-router-dom";
import { validateInvoiceNumber } from "./invoice-constants"; //M10-11021
import { einvCategoryMap, getFinancialYear, isValidKeyboardInput, getLiteralNowIso, isEwayBillPending, EWAY_BILL_PENDING_MESSAGE } from "../invoice-master/invoice-helper"; //M10-12861, M10-16202
import EwayDetails from "./InvoiceEntryCards/EwayDetails"; //M10-16202
import editPen from "src/assets/edit-pen.svg";
import VerifyPoRefModal, { usePoRefVerification } from "./InvoiceEntryCards/VerifyPoRefModal"; //M10-16162
import { useCounterSelection } from "src/hooks/useCounterSelection";
import { DateTimeInput } from "src/features/fields/components/DateTimeInput";
import useSingleFetchAutocomplete from "src/hooks/useSingleFetchAutocomplete";   // M10-15867

const defaultTableData = [
  { label: "Company", master: "", invoice: "" },
  { label: "Division Name", master: "", invoice: "" },
  { label: "Invoice No.", master: "", invoice: "" },
  { label: "Invoice Date", master: "", invoice: "", invoiceDate: "" },
  { label: "FUID", master: "", invoice: "" },
  // { label: "Reference ID", master: "", invoice: "" },
  { label: "Doctor name", master: "", invoice: "" },
  { label: "Link Type", master: "", invoice: "" },
  { label: "Counter Code", master: "", invoice: "" },
  { label: "Counter Name", master: "", invoice: "" },
  { label: "Clinic/Hospital", master: "", invoice: "" },
  { label: "Order Code", master: "", invoice: "" },
  { label: "Stockist", master: "", invoice: "" },
  { label: "Stockist Code", master: "", invoice: "" },
  { label: "Pharmacy name", master: "", invoice: "" },
  { label: "Address", master: "", invoice: "" },
  { label: "City", master: "", invoice: "" },
  { label: "MCI No.", master: "", invoice: "" },
  { label: "MCI Validated", master: "", invoice: "" },
  { label: "DL Number*", master: "", invoice: "" },
  { label: "DL Present", master: "", invoice: "" },
  { label: "GST No.*", master: "", invoice: "" },
  { label: "GST Applicability", master: "", invoice: "" },  //M10-11546
  { label: "E-Inv.Applicability", master: "", invoice: "" },
  { label: "E-Inv.Exempt", master: "", invoice: "" },
  { label: "QR/IRN", master: "", invoice: "" },
  { label: "IRN Number", master: "", invoice: "" },
  { label: "Entered By", master: "", invoice: "" },
  { label: "Approved By", master: "", invoice: "" },
  { label: "Net Amount", master: "", invoice: "" },
  { label: "E-Inv Category", master: "", invoice: "" },
];

const InvoiceHeader = () => {
  const formatBoolean = (value: boolean | null | undefined) => {
    if (value === true) return "Yes";
    if (value === false) return "No";
    return "-";
  };
  const [tableData, setTableData] = useState<any[]>(defaultTableData);
  const { invoiceDetails, invoiceMappingOrderedProducts, invoiceMappingOrderData } = useSelector((state: RootState) => state.invoice);
  const [invoiceNumber, setInvoiceNumber] = useState<string>(invoiceDetails?.invoice?.invoice_number);
  const [netAmount, setNetAmount] = useState<number>(invoiceDetails?.invoice?.net_amount);
  const [invoiceDate, setInvoiceDate] = useState<string>(invoiceDetails?.invoice?.new_invoice_date);
  // Mandatory only for KAM (module_code 4) invoices; must always be >= Invoice Date.
  // Defaults to Invoice Date (when one exists) but the user can freely change the date and/or time from there.
  const [goodsDeliveredDate, setGoodsDeliveredDate] = useState<string>(invoiceDetails?.invoice?.goods_delivered_date || invoiceDetails?.invoice?.new_invoice_date);
  // Available: user picks their own date, editable. Not Available: mirrors Invoice Date, read-only.
  // Hydrated from the saved value when one exists; null (no default) only for a brand-new invoice
  // that has never had this field set — the user must then explicitly pick one before submitting.
  const [goodsDeliveredAvailability, setGoodsDeliveredAvailability] = useState<string | null>(
    invoiceDetails?.invoice?.is_goods_delivered_date_available === true
      ? "Available"
      : invoiceDetails?.invoice?.is_goods_delivered_date_available === false
      ? "Not Available"
      : null
  );
  const [dlValidated, setdlValidated] = useState<string>(invoiceDetails?.invoice?.dl_validated === true ? "Yes" : "No");
  const [mciValid, setMciValid] = useState<string>(invoiceDetails?.invoice?.mci_validated === true ? "Yes" : "No");
  const [gstStatus, setGstStatus] = useState<string>(invoiceDetails?.invoice?.gst_validated === true ? "Yes" : "No");
  // PUC (module_code 3) invoices: MCI Validated, Stamp & Sign Status and E-Inv are locked; the latter two
  // default to "No" - same as the entry screen (invoice-detail.component.tsx)
  const isPucModule = Number(invoiceDetails?.module_code) === 3;
  const [StampSeal, setStampSeal] = useState<any>(isPucModule ? "No" : formatBoolean(invoiceDetails?.invoice?.stamp_and_seal_validated));
  const [category1, setCategory1] = useState<any>(isPucModule ? "No" : formatBoolean(invoiceDetails?.invoice?.is_category_1));
  useEffect(() => {
    if (!isPucModule) return;
    setStampSeal("No");
    setCategory1("No");
  }, [isPucModule]);
  const [netAmountValidate, setNetAmountValidate] = useState<boolean>(invoiceDetails?.invoice?.net_amount_validated); //M10-11051
  const [qrIRN, setQrIRN] = useState<string>(invoiceDetails?.invoice?.e_inv_ref_type);
  const [irnNumber, setIrnNumber] = useState<string>(invoiceDetails?.invoice?.irn);
  //M10-15732
  const [poRefNumber, setPoRefNumber] = useState<string>(invoiceDetails?.poid_ref_number || "");
  const [isPoRefMatched, setIsPoRefMatched] = useState<boolean>(invoiceDetails?.is_poid_ref_matched === true);
  const [selectedStockistPoAttachmentId, setSelectedStockistPoAttachmentId] = useState<number | null>(
    invoiceDetails?.stockist_po_attachment_id ?? null
  );
  // M10-15818: order_status_id of the PO picked from the PO Ref No dropdown — 3 means the linked
  // order is already fully invoiced, so submitting the header against it must be blocked.
  const [selectedPoRefOrderStatusId, setSelectedPoRefOrderStatusId] = useState<number | null>(null);
  //M10-15732
  const poRefVerification = usePoRefVerification(poRefNumber); //M10-16162
  const { toast } = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { withEventCheck } = useEventStatus();
  const [isLoading, setIsLoading] = useState(false);
  const [escalateToMasterOpen, setEscalateToMasterOpen] = useState<boolean>(false);
  const [rejectOpen, setRejectOpen] = useState<boolean>(false);
  const [isErrorInvoice, setIsErrorInvoice] = useState("");  //M10-12999
  const [textractFilePath, setTextractFilePath] = useState<string>("");
  // M10-16202: E-Way Bill popup, opened the same way as on the Invoice Entry screen
  const [isEwayModalOpen, setIsEwayModalOpen] = useState<boolean>(false);
  // Set once the user saves the E-Way Bill in the popup, so the header can be submitted without
  // reloading the invoice. A null eWayBillDtl counts as not verified.
  const [isEwayVerified, setIsEwayVerified] = useState<boolean>(false);
  useEffect(() => {
    setIsEwayVerified(false);
  }, [invoiceDetails?.stockist_invoice_attachment_id]);
  const isEwayPending = !isEwayVerified && isEwayBillPending(invoiceDetails);
  useEffect(() => {
    if (isEwayPending && invoiceDetails?.is_eway_applicable) setIsEwayModalOpen(true);
  }, [invoiceDetails?.stockist_invoice_attachment_id, isEwayPending, invoiceDetails?.is_eway_applicable]);
  //M10-15262
  // Verification is tracked in redux (keyed by invoice_id) instead of local state so it
  // survives tab switches and screen navigation while the user works through the same invoice.
  const invoiceVerificationId = invoiceDetails?.invoice?.invoice_id ?? null;
  const { invoiceVerification } = useSelector((state: RootState) => state.invoice);
  const isCurrentInvoiceVerification = invoiceVerification?.invoiceId === invoiceVerificationId;
  const isInvoiceDownloaded = isCurrentInvoiceVerification && invoiceVerification.isDownloaded;
  const isInvoiceNumberVerified = isCurrentInvoiceVerification && invoiceVerification.isNumberVerified;
  const isInvoiceDateVerified = isCurrentInvoiceVerification && invoiceVerification.isDateVerified;
  const setIsInvoiceNumberVerified = (value: boolean) =>
    dispatch(invoiceActions.setInvoiceVerification({ invoiceId: invoiceVerificationId, isNumberVerified: value }));
  const setIsInvoiceDateVerified = (value: boolean) =>
    dispatch(invoiceActions.setInvoiceVerification({ invoiceId: invoiceVerificationId, isDateVerified: value }));
  // Re-entering the number/date after a prior verification only needs re-verifying
  // that same field, not a fresh download and not re-verifying the other field.
  const relockNumberVerification = () => {
    dispatch(invoiceActions.setInvoiceVerification({
      invoiceId: invoiceVerificationId,
      isNumberVerified: false,
    }));
  };
  const relockDateVerification = () => {
    dispatch(invoiceActions.setInvoiceVerification({
      invoiceId: invoiceVerificationId,
      isDateVerified: false,
    }));
  };
  //M10-15262
  const {
    isNmvYetToProcess,
    allowCustomerNameEntry,
    counterList,
    selectedCounter,
    isCounterModalOpen,
    tempSelectedCounter,
    setTempSelectedCounter,
    openCounterModal,
    closeCounterModal,
    confirmCounterSelection,
    counterPayloadFields,
  } = useCounterSelection({
    companyCode: invoiceDetails?.company_code,
    stockistId: invoiceDetails?.stockist_id,
    uploadType: invoiceDetails?.upload_type,
    fuidStatusId: invoiceDetails?.fileStatusHdr?.fuid_status_id,
    moduleCode: invoiceDetails?.module_code,
    existingCounterCode: invoiceDetails?.counter_code,
    existingCounterName: invoiceDetails?.counter_name,
    existingCustomerName: invoiceDetails?.customer_name,
  });
  // Whether the Counter Name row currently represents a walk-in customer entry rather than a real counter
  const isCustomerNameEntry = Boolean(selectedCounter?.isCustomerEntry || (!selectedCounter && invoiceDetails?.customer_name));
  const { hasPermission: hasPermissionInvoicePreDate} = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE.INVOICEPREDATE.module,
    action: fineGrainedPermissionsKeys.INVOICE.INVOICEPREDATE.action,
  });
  const { hasPermission: hasPermissionEditCounter } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE.EDITCOUNTER.module,
    action: fineGrainedPermissionsKeys.INVOICE.EDITCOUNTER.action,
  });

  //M10-11086
  // const [IRNValidation, setIRNValidation] = useState<boolean>(
  //   invoiceDetails?.invoice?.e_inv_ref_type === "IRN" ||
  //   invoiceDetails?.invoice?.e_inv_ref_type === "BOTH"
  // );
  // const [IRNExempt, setIRNExempt] = useState<any>(invoiceDetails?.invoice?.irn_exempt);
  //M10-11086

  const catTwoAndThree =
    invoiceDetails?.einvack_type_code === "Upload E-Inv Aknowledgement Copy separately" ||
    invoiceDetails?.einvack_type_code === "Periodic Bulk Upload of E-Invoice";

  const company_code = invoiceDetails?.company_code;

  // M10-15867: getPoRefNoListing has no real pagination and always returns the same full list —
  // fetch it once per counter/location/date/company and skip re-fetching on every
  // keystroke/scroll/dropdown-open the Autocomplete triggers.
  const fetchPoRefList = useSingleFetchAutocomplete(
    () =>
      getPoRefNoListing({
        counter_code: invoiceDetails?.counter_code,
        location_code: invoiceDetails?.location_code,
        invoice_date: invoiceDate,
        company_code,
      }),
    [invoiceDetails?.counter_code, invoiceDetails?.location_code, invoiceDate, company_code]
  );
  //M10-15732: shared by the PO Ref No dropdown and the M10-16162 verify popup's suggested matches
  const handlePoRefChange = (value: any) => {
    const isListedPo = !!value && !value.isCustom;
    setPoRefNumber(value?.poid_ref_number || "");
    setIsPoRefMatched(isListedPo);
    setSelectedStockistPoAttachmentId(isListedPo ? value.stockist_po_attachment_id ?? null : null);
    setSelectedPoRefOrderStatusId(isListedPo ? value.order_status_id ?? null : null); //M10-15818
  };

  const fetchTextractData = async () => {
    // M10-16244: the textract API returns 400 without an attachment ID or FUID
    if (!invoiceDetails?.stockist_invoice_attachment_id && !invoiceDetails?.fuid_no) return;
    try {
      const response = await getTextractData(
        { stockist_invoice_attachment_id: invoiceDetails?.stockist_invoice_attachment_id, fuid: invoiceDetails?.fuid_no },
        company_code
      );
      const textractHeaderData = response?.data?.data?.data?.header;
      setTextractFilePath(textractHeaderData?.file_path || "");
      if (
        invoiceDetails?.counter_gst_number?.trim().toUpperCase() ===
        textractHeaderData?.retailer_gst?.trim().toUpperCase() && invoiceDetails?.invoice?.gst_validated === false
      ) {
         setGstStatus("Yes");
      }
      // Default the editable field to the Generated (OCR) invoice number once it loads, but only
      // if the user hasn't already started editing it — otherwise their input gets clobbered.
      setInvoiceNumber((prev) =>
        prev === invoiceDetails?.invoice?.invoice_number ? textractHeaderData?.invoice_number || prev : prev
      );
      const newTableData = [
        { label: "Company", master: invoiceDetails?.company_name, invoice: textractHeaderData?.company_name },
        {
          label: "Division Name",
          master: invoiceMappingOrderData?.flatMap((item: any) => item?.division_name)?.join(", ") || "",
          invoice: invoiceMappingOrderData?.flatMap((item: any) => item?.division_name)?.join(", ") || "",
        },
        { label: "Invoice no.", master: invoiceDetails?.invoice?.invoice_number, invoice: textractHeaderData?.invoice_number },
        {
          label: "Invoice date",
          master: moment(invoiceDetails?.invoice?.new_invoice_date).format("MMMM Do YYYY, h:mm a"),
          invoice: moment(textractHeaderData?.invoice_date).format("MMMM Do YYYY, h:mm a"),
          invoiceDate: invoiceDetails?.invoice?.new_invoice_date,
        },
        { label: "FUID", master: invoiceDetails?.fuid_no, invoice: textractHeaderData?.fuid },
        // { label: "Reference ID", master: "", invoice: "" },
        // { label: "Doctor name", master: "", invoice: "" },
        { label: "Link Type", master: invoiceDetails?.link_type, invoice: "" },
        { label: "Counter Code", master: invoiceDetails?.counter_code, invoice: textractHeaderData?.counter_code },
        { label: "Counter Name", master: invoiceDetails?.counter_name || invoiceDetails?.customer_name, invoice: invoiceDetails?.link_type !== "DRAPH" ? textractHeaderData?.retailer_name : '' },
        { label: "Clinic/Hospital", master: invoiceDetails?.counter_clinic_name, invoice: textractHeaderData?.clinic_name },
        ...(invoiceDetails?.link_type === "INDPH-HS" ? [
          { label: "Hospital Name", master: invoiceDetails?.hospital_name, invoice: textractHeaderData?.hospital_name },
        ] : []),
        {
          label: "Order Code",
          master: invoiceDetails?.orderCodes?.map((code: any) => code?.order_code).join(", ") || "-",
          invoice: textractHeaderData?.order_code,
        },

        { label: "Stockist", master: invoiceDetails?.stockist_name, invoice: textractHeaderData?.stockist_name },
        { label: "Stockist Code", master: invoiceDetails?.stockist_medvol_code, invoice: textractHeaderData?.stockist_phcode },
        { label: "Pharmacy name", master: invoiceDetails?.pharmacy_name, invoice: invoiceDetails?.link_type == "DRAPH" ? textractHeaderData?.retailer_name : '' },
        { label: "Address", master: invoiceDetails?.counter_address, invoice: textractHeaderData?.retailer_address },
        { label: "City", master: invoiceDetails?.state_district_city?.split(",")[2], invoice: "" },
        { label: "MCI No.", master: invoiceDetails?.counter_mci_number, invoice: textractHeaderData?.retailer_mci_number },
        { label: "MCI Validated", master: invoiceDetails?.mci_validated, invoice: "" },
        { label: "DL Number*", master: invoiceDetails?.counter_dl_number, invoice: textractHeaderData?.retailer_dl_number },
        { label: "DL Present", master: invoiceDetails?.invoice?.dl_validated, invoice: "" },
        { label: "GST No.*", master: invoiceDetails?.counter_gst_number, invoice: textractHeaderData?.retailer_gst },
        { label: "GST Applicability", master: invoiceDetails?.counter_gst_status ? "Yes" : "No", invoice: "" }, //M10-11546
        { label: "E-Inv.Applicability", master: invoiceDetails?.is_einv_applicable === true ? "Yes" : "No", invoice: "" },
        { label: "E-Inv.Exempt", master: invoiceDetails?.orderCodes?.some((item: any) => item?.reference_order?.novu_metadata?.e_inv_required == "Y") ? invoiceDetails.orderCodes.filter((item: any) => item?.reference_order?.novu_metadata?.e_inv_required == "Y").every((item: any) => item?.reference_order?.einv_exempt === true) ? "Yes" : "No" : "-", invoice: "" },
        { label: "QR/IRN", master: invoiceDetails?.invoice?.e_inv_ref_type, invoice: "" },
        { label: "IRN Number", master: invoiceDetails?.invoice?.irn, invoice: "" },
        { label: "Entered By", master: invoiceDetails?.invoice?.entered_by, invoice: "" },
        { label: "Approved By", master: invoiceDetails?.invoice?.approved_by, invoice: "" },
        ...(invoiceDetails?.is_einv_applicable === true ? [
          { label: "E-Inv Category", master: einvCategoryMap[invoiceDetails?.einvack_type_code] || "-", invoice: "" },
        ] : []),
        { label: "Net Amount", master: "", invoice: "" },
        ...(invoiceDetails?.stamp_and_seal_required === true ? [
          { label: "Stamp & Sign Status", master: "", invoice: "" },
          { label: "E-Inv", master: "", invoice: "" }
        ] : []),
        // Patient Details applies only to module_code 3 invoices
        ...(Number(invoiceDetails?.module_code) === 3 ? [{ label: "Patient Details", master: `${invoiceDetails?.orderCodes?.[0]?.reference_order?.novu_metadata?.patient_details?.name || "-"}-${invoiceDetails?.orderCodes?.[0]?.reference_order?.novu_metadata?.patient_details?.age || "-"}/${invoiceDetails?.orderCodes?.[0]?.reference_order?.novu_metadata?.patient_details?.gender || "-"}`, invoice: "" }] : []),
        // Only applicable/mandatory for KAM (module_code 4) invoices — kept as the last row on screen
        ...(invoiceDetails?.module_code === 4 ? [{
          label: "Goods Delivered Date*",
          // .utc() — the stored value is a literal wall-clock stamp (see DateTimeInput), not a true UTC instant;
          // formatting via plain moment() would re-apply the browser's local offset and show the wrong time.
          master: "",
          invoice: "",
          goodsDeliveredDate: invoiceDetails?.invoice?.goods_delivered_date || invoiceDetails?.invoice?.new_invoice_date,
        },
        //M10-15732
        { label: "PO Ref No", master: "", invoice: "" },
        //M10-15732
        ] : [])
      ];
      setTableData(newTableData);
    } catch (error: any) {
      console.log(error);
      toast({ description: error?.response?.data?.message || "Error fetching MV Lens data", variant: "destructive" });
    }
  };
  const isRequestInProgress = useRef(false);

  const handleSubmitInvoiceHeader = useDebounceLeading(async () => {
    if (isRequestInProgress.current) return;
    isRequestInProgress.current = true;
    try {
      //M10-15262
      if (!isInvoiceNumberVerified || !isInvoiceDateVerified) {
        toast({ description: "Please download the invoice and verify the Invoice Number and Invoice Date", variant: "destructive" });
        return;
      }
      //M10-15262
      // M10-13321
      if (invoiceDate < get120DaysAgoDate(invoiceDetails?.upload_type) && !hasPermissionInvoicePreDate) {
        toast({
          description:
            invoiceDetails?.upload_type === "INST"
              ? "Invoice date cannot be before Dec 1, 2025"
              : "Invoice date must be within the last 120 days",
          variant: "destructive",
        });
        return;
      }
      // M10-13321
      if (isNmvYetToProcess && !selectedCounter && !invoiceDetails?.counter_code && !invoiceDetails?.customer_name) {
        toast({
          description: allowCustomerNameEntry ? "Please select a Counter or enter Customer Name" : "Please select a Counter",
          variant: "destructive",
        });
        return;
      }
      //M10-11021
      let vertifyResult = validateInvoiceNumber(invoiceNumber)
      if (!vertifyResult.valid) {
        toast({ description: vertifyResult?.error, variant: "destructive" });
        return;
      }
      //M10-11021
      //M10-12999
      if (Boolean(isErrorInvoice)) {
        toast({ description: isErrorInvoice, variant: "destructive" });
        return;
      }
      //M10-12999
      // M10-16202
      if (isEwayPending) {
        toast({ description: EWAY_BILL_PENDING_MESSAGE, variant: "destructive" });
        if (invoiceDetails?.is_eway_applicable) setIsEwayModalOpen(true);
        return;
      }
      if (invoiceDate == undefined || invoiceDate == "" || invoiceDate == null || invoiceDate == " ") {
        toast({ description: "Select Invoice Date before updating", variant: "destructive" });
        return;
      }

      // Mandatory only for KAM (module_code 4) invoices
      if (invoiceDetails?.module_code === 4 && !goodsDeliveredAvailability) {
        toast({ description: "Select Goods Delivered Date availability before updating", variant: "destructive" });
        return;
      }
      if (invoiceDetails?.module_code === 4 && !goodsDeliveredDate) {
        toast({ description: "Select Goods Delivered Date before updating", variant: "destructive" });
        return;
      }
      if (invoiceDetails?.module_code === 4 && new Date(goodsDeliveredDate) < new Date(invoiceDate)) {
        toast({ description: "Goods Delivered Date cannot be before Invoice Date", variant: "destructive" });
        return;
      }
      //M10-15818
      if (selectedPoRefOrderStatusId === 3) {
        toast({ description: "Order is fully invoiced", variant: "destructive" });
        return;
      }
      //M10-15818
      //M10-16162
      if (
        invoiceDetails?.module_code === 4 &&
        invoiceDetails?.fileStatusHdr?.fuid_status_id !== 8 &&
        poRefNumber?.trim() &&
        !isPoRefMatched &&
        !poRefVerification.isVerified()
      ) {
        await poRefVerification.open(fetchPoRefList);
        return;
      }
      //M10-16162
      if (invoiceDetails?.module_code === 4 && new Date(goodsDeliveredDate) > new Date(getLiteralNowIso())) {
        toast({ description: "Goods Delivered Date cannot be in the future", variant: "destructive" });
        return;
      }

      //M10-11134
      if (new Date(invoiceDate) > new Date()) {
        toast({ description: "Invoice date cannot be in the future", variant: "destructive" });
        return;
      }
      //M10-11134

      // .every() on [] is vacuously true, so require at least one e-inv-required order before treating the invoice as exempt
      const einvRequiredOrders = invoiceDetails?.orderCodes?.filter((codes: any) => codes?.reference_order?.novu_metadata?.e_inv_required == "Y") ?? [];
      const isEinvExempt = einvRequiredOrders.length > 0 && einvRequiredOrders.every(
        (item: any) => item?.reference_order?.einv_exempt === true
      );

      if (!isEinvExempt && (((!qrIRN?.trim() || qrIRN == null) && invoiceDetails?.is_einv_applicable == true && !catTwoAndThree) || (category1 == 'Yes' && (!qrIRN?.trim() || qrIRN == null)))) { //M10-13053
        toast({ description: "Add QR/IRN", variant: "destructive" });
        return;
      }

      //M10-11086
      // if (!IRNExempt) {
      if (
        (irnNumber?.trim() !== 'IMAGE FILE' &&
          invoiceDetails?.is_einv_applicable === true &&
          !catTwoAndThree &&
          qrIRN?.trim() &&
          qrIRN !== "QR" &&
          !isEinvExempt &&
          irnNumber?.trim().length !== 64) || (
          irnNumber?.trim() !== 'IMAGE FILE' &&
          category1 =='Yes' &&  //M10-13053
          qrIRN?.trim() &&
          qrIRN !== "QR" &&
          !isEinvExempt &&
          irnNumber?.trim().length !== 64)
      ) {
        toast({ description: "IRN should be 64 digit", variant: "destructive" });
        return;
      }
      // }

      // M10-12476 
      const amount = Number(netAmount);
      let errorMessage = "";
      if (netAmount === undefined || netAmount === null || isNaN(amount)) {
        errorMessage = "Enter a valid Net Amount";
      } else if (amount <= 0) {
        errorMessage = "Net Amount must be greater than 0";
      } else if (amount > 2500000) {
        errorMessage = "Net Amount exceeds the limit of 25,00,000";
      } else if (!/^\d+(\.\d{1,2})?$/.test(String(netAmount))) {
        errorMessage = "Net Amount can have only 2 decimal places";
      }
      if (/[-_]\s?1$/.test(invoiceNumber)) {
        errorMessage = "Invoice number cannot end with -1 or - 1";
      }
      if (errorMessage) {
        toast({
          description: errorMessage,
          variant: "destructive",
        });
        return;
      }
      // M10-12476

      if (!netAmountValidate) {
        toast({ description: "Validate Net Amount as YES", variant: "destructive" });
        return;
      }

      // if (((qrIRN == "IRN" || qrIRN == "BOTH") && (irnNumber == " " || irnNumber == ""))) {
      //   toast({ description: "Add IRN Number", variant: "destructive" });
      //   return;
      // }

      // if (gstStatus == "Yes" && !invoiceDetails?.counter_gst_number?.trim()) {
      //   toast({ description: "GST not validated", variant: "destructive" });
      //   return;
      // }
      // PUC (module_code 3) invoices skip GST / MCI / DL validations
      if (!isPucModule && gstStatus == "No" && invoiceDetails?.counter_gst_status) {
        toast({ description: "Validate GST Applicability as YES", variant: "destructive" });  //M10-11546
        return;
      }
      // if (dlValidated == "Yes" && !invoiceDetails?.counter_dl_number?.trim()) {
      //   toast({ description: "DL not validated", variant: "destructive" });
      //   return;
      // }
      // if (mciValid == "Yes" && !invoiceDetails?.counter_mci_number?.trim()) {
      //   toast({ description: "MCI not validated", variant: "destructive" });
      //   return;
      // }

      // if (invoiceDetails?.link_type == "DRDS" && mciValid == "Yes" && !invoiceDetails?.counter_mci_number?.trim()) {
      //   toast({ description: "MCI not validated", variant: "destructive" });
      //   return;
      // }
      if (!isPucModule && invoiceDetails?.link_type == "DRDS" && mciValid == "No" && invoiceDetails?.counter_mci_number?.trim()) {
        toast({ description: "Validate MCI Validated as YES", variant: "destructive" });
        return;
      }
      // if (invoiceDetails?.link_type == "DRDS" && mciValid == "No" && !invoiceDetails?.counter_mci_number?.trim()) {
      //   toast({ description: "MCI not validated", variant: "destructive" });
      //   return;
      // }
      // if (invoiceDetails?.link_type == "DRDS" && gstStatus == "Yes" && !invoiceDetails?.counter_gst_number?.trim()) {
      //   toast({ description: "GST not validated", variant: "destructive" });
      //   return;
      // }
      // if (
      //   (invoiceDetails?.link_type == "DRAPH" || invoiceDetails?.link_type == "INDPH-PH" || invoiceDetails?.link_type == "INDPH-HS") &&
      //   dlValidated == "Yes" &&
      //   (!invoiceDetails?.counter_dl_number?.trim() || invoiceDetails?.counter_dl_number?.trim() == undefined)
      // ) {
      //   toast({ description: "DL not validated", variant: "destructive" });
      //   return;
      // }
      // if (
      //   (invoiceDetails?.link_type == "DRAPH" || invoiceDetails?.link_type == "INDPH-PH" || invoiceDetails?.link_type == "INDPH-HS") &&
      //   dlValidated == "No" &&
      //   (!invoiceDetails?.counter_dl_number?.trim() || invoiceDetails?.counter_dl_number?.trim() == undefined)
      // ) {
      //   toast({ description: "DL not validated", variant: "destructive" });
      //   return;
      // }
      if (
        !isPucModule &&
        (invoiceDetails?.link_type == "DRAPH" || invoiceDetails?.link_type == "INDPH-PH" || invoiceDetails?.link_type == "INDPH-HS") &&
        dlValidated == "No" &&
        invoiceDetails?.counter_dl_number?.trim()
      ) {
        toast({ description: "Validate DL Present as YES", variant: "destructive" });
        return;
      }
      //M10-13053
       const isValidYesNo = (val:any) => val === "Yes" || val === "No";
      const invalidFields = [];
      if (!isValidYesNo(StampSeal)) {
        invalidFields.push("Stamp & Sign Status");
      }
      if (!isValidYesNo(category1)) {
        invalidFields.push("E-Inv");
      }
      if (invalidFields.length > 0 && invoiceDetails?.stamp_and_seal_required) {
        toast({
          description: `Please select Yes or No for: ${invalidFields.join(", ")}`,
          variant: "destructive",
        });
        return;
      }
      //M10-13053
      // if (
      //   (invoiceDetails?.link_type == "DRAPH" || invoiceDetails?.link_type == "INDPH-PH" || invoiceDetails?.link_type == "INDPH-HS") &&
      //   gstStatus == "Yes" &&
      //   (!invoiceDetails?.counter_gst_number?.trim() || invoiceDetails?.counter_gst_number?.trim() == undefined)
      // ) {
      //   toast({ description: "GST not validated", variant: "destructive" });
      //   return;
      // }
      setIsLoading(true);
      const currentDate = new Date(invoiceDate);
      const invoiceAccYear = getFinancialYear(currentDate);
      
      const payloadToValidateInV1 = {
        cmpstockistcode: invoiceDetails?.company_stockist_code,
        invoicenumber_searchcolumn: invoiceNumber,
        invoice_accyear: invoiceAccYear,
        company_code: invoiceDetails?.company_code,
      };
      const response = await getInvoiceChangeValidationV1(payloadToValidateInV1);
      if (response?.data?.success !== "success") {
        toast({ description: response?.data?.message, variant: "destructive" });
        setIsLoading(false);
        return;
      }
      const payload = {
        data: {
          invoice_status: invoiceDetails?.fileStatusHdr?.fuid_status_shortcode,
          invoice_id: invoiceDetails?.invoice?.invoice_id,
          invoice_number: invoiceNumber?.trim(),
          u_code: invoiceDetails?.invoice?.u_code,
          new_invoice_date: invoiceDate,
          stockist_invoice_attachment_id: invoiceDetails?.stockist_invoice_attachment_id,
          stockist_id: invoiceDetails?.stockist_id,
          ...(invoiceDetails?.module_code === 4 && {
            goods_delivered_date: goodsDeliveredDate,
            is_goods_delivered_date_available: goodsDeliveredAvailability === "Available",
          }),
          ...(qrIRN?.trim() !== "" && { e_inv_ref_type: qrIRN }),
          irn: qrIRN == "IRN" || qrIRN == "BOTH" ? irnNumber.trim() : "",
          gst_validated: gstStatus == "Yes" ? true : false,
          dl_validated: dlValidated == "Yes" ? true : false,
          mci_validated: mciValid == "Yes" ? true : false,
          counter_gst_number: invoiceDetails?.counter_gst_number || "",
          counter_dl_number: invoiceDetails?.counter_dl_number || "",
          counter_mci_number: invoiceDetails?.counter_mci_number || "",
          net_amount: `${Number(netAmount).toFixed(2)}`,
          header_edit: true,
          // invoice_num_modified: invoiceDetails?.invoice?.invoice_number?.trim() !== invoiceNumber?.trim(),
          invoice_num_modified: true,
          stamp_and_seal_validated: StampSeal== "Yes" ? true : false, //M10-13053
          is_category_1: category1 == "Yes" ? true : false, //M10-13053
          net_amount_validated: netAmountValidate, //M10-11051
          irn_exempt: false, //M10-11086
          //M10-15732
          ...(invoiceDetails?.module_code === 4 && {
            poid_ref_number: poRefNumber?.trim() || null,
            is_poid_ref_matched: isPoRefMatched,
            stockist_po_attachment_id: selectedStockistPoAttachmentId,
          }),
          //M10-15732
          counter_data_validation:
            invoiceDetails?.invoice?.dl_validated !== (dlValidated == "Yes" ? true : false) ||
            invoiceDetails?.invoice?.gst_validated !== (gstStatus == "Yes" ? true : false) ||
            invoiceDetails?.invoice?.mci_validated !== (mciValid == "Yes" ? true : false),
          ...counterPayloadFields,
        },
        primary_key: invoiceDetails?.invoice?.invoice_id,
        event_type: "INVOICE_EDIT",
      };
      
      const res = await editInvoice(payload, company_code);

      const responseEvent = await withEventCheck(
        () => {
          return getInvoiceDetails(invoiceDetails?.stockist_invoice_attachment_id as string, false, company_code);
        },
        res?.data?.data?.invoice?.eventStreamCreated?.streamId,
        company_code  //M10-12758
      );
      dispatch(invoiceActions?.setInvoiceDetails(responseEvent?.data?.data));
      toast({ description: "Invoice updated successfully", variant: "default" });
    } catch (error: any) {
      console.log(error);
      toast({ description: error?.responseEvent?.data?.message || error?.response?.data?.message || "Failed to update invoice", variant: "destructive" });
    } finally {
      setIsLoading(false);
      isRequestInProgress.current = false;
    }
  }, 1000);

  const fetchOrderedProducts = async () => {
    try {
      const resp = await getOrderProductsForMapping(invoiceDetails?.orderCodes?.map((code: any) => code?.order_code).join(", "), company_code); //M10-10771
      // dispatch(invoiceActions?.setInvoiceMappingOrderedProducts(resp?.data?.data?.flatMap((item: any) => item?.orderProducts)));
      const productsWithDivision = Array.from(
        new Map(
          resp?.data?.data
            ?.flatMap((item: any) =>
              item?.orderProducts?.map((product: any) => ({
                ...product,
                item_division: item?.division_name,
              }))
            )
            ?.map((item: any) => [item.item_code, item])
        ).values()
      );
      dispatch(invoiceActions?.setInvoiceMappingOrderedProducts(productsWithDivision));
      dispatch(invoiceActions?.setInvoiceMappingOrderData(resp?.data?.data));
    } catch (error: any) {
      toast({ description: error?.response?.data?.message || "Error fetching division codes", variant: "destructive" });
      console.log(error);
    }
  };

  const handleInputChange = (e: any) => {
    const inputValue = e.target.value;
    //M10-12999
    let errorMessage = "";

    if (/[-_]\s?1$/.test(inputValue)) {
      errorMessage = "Invoice number cannot end with -1 or - 1";
    }

    if (inputValue.length > 35) {
      errorMessage = "Invoice number must be 35 characters";
    } else if (!isValidKeyboardInput(inputValue)) {
      errorMessage = "Invalid characters";
    }

    setIsErrorInvoice(errorMessage);
    //M10-12999
    setInvoiceNumber(inputValue);
    relockNumberVerification(); //M10-15262
  };
  const handleNetAmount = (e: any) => {
    const inputValue = e.target.value;
    setNetAmount(inputValue);
  };

  useEffect(() => {
    if (invoiceMappingOrderedProducts?.length === 0 && invoiceDetails?.orderCodes?.length > 0) {
      fetchOrderedProducts();
    }
    fetchTextractData();
  }, []);

  // Goods Delivered Date: when marked Not Available, it always mirrors Invoice Date
  useEffect(() => {
    if (goodsDeliveredAvailability === "Not Available") {
      setGoodsDeliveredDate(invoiceDate);
    }
  }, [goodsDeliveredAvailability, invoiceDate]);

  // useEffect(() => {
  //   if (invoiceMappingOrderedProducts?.length > 0) {
  //     fetchTextractData();
  //   }
  // }, [invoiceMappingOrderedProducts]);


  return (
    <div>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          textAlign: "left",
          border: "1px solid #ccc",
        }}
        className="mt-10"
      >
        <thead>
          <tr className="text-sm bg-[#F1F1F1]">
            <th style={{ padding: "10px", borderBottom: "1px solid #ccc" }}></th>
            <th style={{ padding: "10px", borderBottom: "1px solid #ccc" }}>As per Master</th>
            <th style={{ padding: "10px", borderBottom: "1px solid #ccc" }}>Generated Invoice</th>
            <th style={{ padding: "10px", borderBottom: "1px solid #ccc" }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {tableData.map((row, index) => (
            <tr key={index} className="w-full">
              <td style={{ padding: "10px", borderBottom: "1px solid #ccc" }} className="w-1/4 text-gray-500">
                <span
                  className={`ml-2 text-sm ${row.label === "Pharmacy name" || row.label === "Counter Name"
                    ? "font-bold"
                    : "font-normal"
                    }`}
                >
                  {row.label === "Counter Name" && isCustomerNameEntry ? "Customer Name" : row.label}
                </span>
              </td>
              <td style={{ padding: "10px", borderBottom: "1px solid #ccc" }} className="w-1/4 text-sm">
                {(() => {
                  if (selectedCounter?.isCustomerEntry) {
                    if (row.label === "Counter Name") return selectedCounter.customer_name;
                    if (["Counter Code", "Address", "DL Number*", "GST No.*", "MCI No.", "Link Type", "City"].includes(row.label)) return "-";
                  }

                  const masterValue = selectedCounter && row.label === "Counter Code"
                    ? selectedCounter.counter_code
                    : selectedCounter && row.label === "Counter Name"
                      ? selectedCounter.counter_name
                      : selectedCounter && row.label === "Address"
                        ? selectedCounter.address
                        : selectedCounter && row.label === "DL Number*"
                          ? selectedCounter.dl_number
                          : selectedCounter && row.label === "GST No.*"
                            ? selectedCounter.gst_number
                            : selectedCounter && row.label === "MCI No."
                              ? selectedCounter.mci_register_number
                              : selectedCounter && row.label === "Link Type"
                                ? selectedCounter.dr_ph_lnk_code
                                : selectedCounter && row.label === "City"
                                  ? selectedCounter.city
                                  : row.master;

                  return row.label === "Counter Code" ? (
                    <a
                      className="text-gray-900 hover:text-[#6b40d6]"
                      href={`/masters/counters/details/?counter_code=${masterValue}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        dispatch(counterActions.setClickType("row"));
                        dispatch(counterActions.setSelectedCounterData({}));
                        window.localStorage.setItem("counterOrderId", masterValue);
                        window.localStorage.setItem("counterCompanyLocationId", invoiceDetails?.counter_company_location_id);
                      }}
                    >
                      {masterValue}
                    </a>
                  ) : masterValue;
                })()}
              </td>
              <td style={{ padding: "10px", borderBottom: "1px solid #ccc" }} className="w-1/4 text-sm">
                {row.invoice}
                {row?.label === "Net Amount" && (
                  <div className="w-[70%]">
                    <Input
                      enableArrows={false}
                      showIcon={false}
                      placeholder={row?.label}
                      type="number"
                      value={netAmount}
                      onChange={handleNetAmount}
                      className="w-full py-1 text-left border border-gray-300 rounded-lg"
                    />
                  </div>
                )}
              </td>
              <td style={{ padding: "10px", borderBottom: "1px solid #ccc" }} className="w-1/4 text-sm">
                {row?.label === "Counter Name" && isNmvYetToProcess && (!invoiceDetails?.counter_code || invoiceDetails?.upload_type !== "INST" || hasPermissionEditCounter) && (
                  <button type="button" onClick={openCounterModal}>
                    <img src={editPen} alt="edit" className="w-4 h-4" />
                  </button>
                )}
                {row?.label === "FUID" && (
                  <Button
                    onClick={() => {
                      if (!textractFilePath) {
                        toast({ description: "MV Lens Output not available", variant: "destructive" });
                        return;
                      }
                      if (!textractFilePath.toLowerCase().endsWith(".json")) {
                        toast({ description: "Only valid JSON files can be downloaded", variant: "destructive" });
                        return;
                      }
                      downloadInvoice(textractFilePath, company_code).then((res: any) => {
                        if (res?.data?.success && res?.data?.data) {
                          window.open(res?.data?.data, "_blank");
                        } else {
                          toast({ description: "Failed to download the MV Lens Output", variant: "destructive" });
                        }
                      });
                    }}
                    className="flex items-center gap-2 px-3 py-1 font-normal bg-[#f1f1f1] border border-gray-300 rounded-md h-8 hover:text-gray-600 focus:outline-none focus:ring-0"
                  >
                    <Download className="w-4 h-4" />
                    <span>MV Lens Output</span>
                  </Button>
                )}
                {row?.label === "Invoice no." && (
                  <div className="w-full flex items-center gap-4">
                    <div className="flex-1">
                      <Input
                        disabled={!isInvoiceDownloaded}
                        enableArrows={false}
                        showIcon={false}
                        placeholder={row?.label}
                        type="text"
                        value={invoiceNumber ?? ""}
                        onChange={handleInputChange}
                        className="w-full py-1 text-left border border-gray-300 rounded-lg"
                        isError={Boolean(isErrorInvoice)}  //M10-12999
                        errorMessage={isErrorInvoice}  //M10-12999
                      />
                    </div>
                    {/* M10-15262 */}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={!isInvoiceDownloaded || !invoiceNumber?.trim()}
                        onClick={() => setIsInvoiceNumberVerified(true)}
                        title={!invoiceNumber?.trim() ? "Nothing to verify" : isInvoiceNumberVerified ? "Verified" : "Click to verify"}
                        className={!isInvoiceDownloaded || !invoiceNumber?.trim() ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
                      >
                        {isInvoiceNumberVerified ? (
                          <CheckCircle size={20} color="green" />
                        ) : (
                          <Circle size={20} className="text-gray-300" />
                        )}
                      </button>
                    </div>
                    {/* M10-15262 */}
                  </div>
                )}
                {row?.label === "Net Amount" && (
                  <div className="w-[100%]">
                    <Toggle
                      checked={netAmountValidate}
                      onChange={(e: any) => {
                        setNetAmountValidate(e === true);
                      }}
                    />
                  </div>
                )}
                {row?.label === "Invoice date" && (
                  <div className="w-full flex items-center gap-4">
                    <div className="flex-1">
                      <DatePicker
                        disabled={!isInvoiceDownloaded}
                        placeholder="dd-mm-yyyy"
                        value={invoiceDate ? invoiceDate.split("T")[0] : row?.invoiceDate }
                        max={moment().format("YYYY-MM-DD")} //M10-14313
                        min={!hasPermissionInvoicePreDate ? get120DaysAgoDate(invoiceDetails?.upload_type) : undefined} // M10-13321
                        onChange={(iso) => {
                          setInvoiceDate(iso);
                          relockDateVerification(); //M10-15262
                        }}
                      />
                    </div>
                    {/* M10-15262 */}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={!isInvoiceDownloaded}
                        onClick={() => setIsInvoiceDateVerified(true)}
                        title={isInvoiceDateVerified ? "Verified" : "Click to verify"}
                        className={!isInvoiceDownloaded ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
                      >
                        {isInvoiceDateVerified ? (
                          <CheckCircle size={20} color="green" />
                        ) : (
                          <Circle size={20} className="text-gray-300" />
                        )}
                      </button>
                    </div>
                    {/* M10-15262 */}
                  </div>
                )}
                {row?.label === "Goods Delivered Date*" && (
                  <div className="w-full flex items-center gap-4">
                    <div className="w-[40%]">
                      <Autocomplete
                        inputClassName="relative"
                        placeholder="Select"
                        defaultValue={goodsDeliveredAvailability}
                        suggestionList={[
                          { key: "Available", value: "Available" },
                          { key: "Not Available", value: "Not Available" },
                        ]}
                        disabled={invoiceDetails?.fileStatusHdr?.fuid_status_id === 8}
                        onSelectionChange={(e: any) => {
                          if (e === null) return;
                          setGoodsDeliveredAvailability(e.value);
                        }}
                        displayKey="key"
                      />
                    </div>
                    <div className="flex-1">
                      {goodsDeliveredAvailability === "Available" && (
                        // Defaults to Invoice Date but stays freely editable — user can pick any date/time >= Invoice Date
                        <DateTimeInput
                          label=""
                          value={goodsDeliveredDate || row?.goodsDeliveredDate}
                          min={invoiceDate || undefined}
                          max={getLiteralNowIso()}
                          onChange={(iso) => setGoodsDeliveredDate(iso)}
                        />
                      )}
                      {goodsDeliveredAvailability === "Not Available" && (
                        <p className="text-[#1B1A1F]">{moment(invoiceDate || row?.goodsDeliveredDate).format("DD MMM, YYYY")}</p>
                      )}
                    </div>
                  </div>
                )}
                {row?.label === "PO Ref No" && (
                  <div className="w-[60%]">
                    {/* M10-15732 */}
                    <Autocomplete
                      inputClassName="relative"
                      placeholder="Select or type PO Ref No"
                      defaultValue={poRefNumber}
                      suggestionList={[]}
                      asyncListFunction={fetchPoRefList}   // M10-15867
                      isAsyncQuery={false}
                      displayKey="poid_ref_number"
                      uniqueKey="stockist_po_attachment_id"
                      allowCustomValue
                      customFieldName="poid_ref_number"
                      disabled={invoiceDetails?.fileStatusHdr?.fuid_status_id === 8}
                      onSelectionChange={handlePoRefChange}
                    />
                    {/* M10-15732 */}
                  </div>
                )}
                {row?.label == "GST Applicability" && ( //M10-11546
                  <div className="w-[60%]">
                    {invoiceDetails?.counter_gst_status &&
                      <Toggle
                        checked={gstStatus == "Yes"}
                        onChange={(e: any) => {
                          setGstStatus(e === true ? "Yes" : "No");
                        }}
                      />}
                  </div>
                )}
                {row?.label == "DL Present" && (
                  <div className="w-[60%]">
                    {tableData.find((d) => d.label === "DL Number*")?.master &&
                      <Toggle
                        checked={dlValidated == "Yes"}
                        onChange={(e: any) => {
                          setdlValidated(e === true ? "Yes" : "No");
                        }}
                      />}
                  </div>
                )}
                {row?.label == "MCI Validated" && (
                  <div className="w-[60%]">
                    {tableData.find((d) => d.label === "MCI No.")?.master && <Toggle
                      disabled={isPucModule}
                      checked={mciValid == "Yes"}
                      onChange={(e: any) => {
                        setMciValid(e === true ? "Yes" : "No");
                      }}
                    />}
                  </div>
                )}
                {row?.label == "QR/IRN" && (
                  <div className="w-[60%] flex items-center gap-2">
                    {/* orderCodes may be empty (e.g. DRAPH invoices without order mapping); Array.every() on [] is vacuously
                        true, so guard on length or an empty invoice would be wrongly treated as e-inv exempt */}
                    {!(invoiceDetails?.orderCodes?.some((item: any) => item?.reference_order?.novu_metadata?.e_inv_required == "Y") && invoiceDetails?.orderCodes?.filter((item: any) => item?.reference_order?.novu_metadata?.e_inv_required == "Y").every((item: any) => item?.reference_order?.einv_exempt === true)) &&
                      <Autocomplete
                        inputClassName="relative"
                        placeholder={row?.label}
                        defaultValue={qrIRN}
                        suggestionList={[
                          { key: "Select", value: "" },
                          { key: "QR", value: "QR" },
                          { key: "IRN", value: "IRN" },
                          { key: "BOTH", value: "BOTH" },
                        ]}
                        disabled={
                          category1 == "No"  && ( //M10-13053
                            invoiceDetails?.is_einv_applicable == false ||
                            [6, 8].includes(invoiceDetails?.fileStatusHdr?.fuid_status_id)
                          )
                        }
                        onSelectionChange={(e: any) => {
                          if (e === null) {
                            setQrIRN("");
                            return;
                          }
                          setQrIRN(e.value);
                          //M10-11086
                          // if (e.value === 'IRN' || e.value === 'BOTH') {
                          //   setIRNValidation(true)
                          // } else {
                          //   setIRNValidation(false)
                          // }
                        }}
                        displayKey="key"
                      />}
                  </div>
                )}
                {/* //M10-11086 */}
                {/*
                {row?.label == "QR/IRN" && IRNValidation && !catTwoAndThree && (
                  <div className="flex items-center gap-2 m-3">
                    <span className="inline-flex items-center gap-2">
                      IRN Exemption :  <Toggle
                        checked={IRNExempt}
                        onChange={(e: any) => {
                          setIRNExempt(e === true);
                        }}
                      />
                    </span>
                  </div>
                )}
                */}
                {(qrIRN === "IRN" || qrIRN === "BOTH") && row?.label === "IRN Number" && (
                  <div className="w-full mt-2">
                    <Input
                      enableArrows={false}
                      showIcon={false}
                      placeholder="IRN Number"
                      type="text"
                      value={irnNumber}
                      disabled={
                        (invoiceDetails?.is_einv_applicable == false ||
                          invoiceDetails?.fileStatusHdr?.fuid_status_id === 8 ||
                          invoiceDetails?.fileStatusHdr?.fuid_status_id === 6) &&
                        category1 == "No" //M10-13053
                      }
                      onChange={(e: any) => setIrnNumber(e.target.value)}
                      className="w-full py-1 text-left border border-gray-300 rounded-lg"
                    />
                  </div>
                )}
                {row?.label == "Stamp & Sign Status" && invoiceDetails?.stamp_and_seal_required && (
                  <div className="w-[60%]">
                    {/* M10-13053 */}
                    <Autocomplete
                      inputClassName="relative"
                      placeholder="Select"
                      defaultValue={StampSeal}
                      suggestionList={[
                        { key: "Select", value: "" },
                        { key: "Yes", value: "Yes" },
                        { key: "No", value: "No" },
                      ]}
                      disabled={isPucModule}
                      onSelectionChange={(e: any) => {
                        if (e === null) {
                          setStampSeal(false);
                          return;
                        }
                        setStampSeal(e.value);
                      }}
                      displayKey="key"
                    />
                    {/* M10-13053 */}
                  </div>
                )}
                {row?.label == "E-Inv" && invoiceDetails?.stamp_and_seal_required && (
                  <div className="w-[60%]">
                    {/* M10-13053 */}
                    <Autocomplete
                      inputClassName="relative"
                      placeholder="Select"
                      defaultValue={category1}
                      suggestionList={[
                        { key: "Select", value: "" },
                        { key: "Yes", value: "Yes" },
                        { key: "No", value: "No" },
                      ]}
                      disabled={isPucModule}
                      onSelectionChange={(e: any) => {
                        if (e === null) {
                          setCategory1(false);
                          return;
                        }

                        const value = e.value;
                        setCategory1(value);

                        if (invoiceDetails?.is_einv_applicable === false && value === 'No') {
                          setQrIRN("");
                          setIrnNumber("");
                          // setIRNValidation(false)
                        }
                      }}
                      displayKey="key"
                    />
                    {/* M10-13053 */}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex justify-end mt-14 gap-x-2">
        {invoiceDetails?.fileStatusHdr?.fuid_status_id !== 2 && (
          <Button
            onClick={() => setRejectOpen(true)}
            disabled={isLoading}
            className="h-9 px-6 text-white font-normal bg-orange-600 shadow-sm transition hover:bg-red-700"
          >
            Reject Invoice
          </Button>
        )}
        <Button onClick={() => setEscalateToMasterOpen(true)} disabled={isLoading} className="font-normal bg-gray-200 border border-gray-300">
          Escalate to Master
        </Button>
        <Button
          disabled={isLoading || isRequestInProgress.current}
          isLoading={isLoading}
          loaderSize="small"
          onClick={handleSubmitInvoiceHeader}
          className="font-normal text-white bg-primary"
          module={fineGrainedPermissionsKeys.INVOICE["UPDATE/APPROVEINVOICE"].module}
          action={fineGrainedPermissionsKeys.INVOICE["UPDATE/APPROVEINVOICE"].action}
        >
          Update Invoice Header
        </Button>
      </div>
      <Modal isOpen={escalateToMasterOpen} isHeader={false} onCloseModal={() => setEscalateToMasterOpen(false)}>
        <EscalateToMaster
          onSuccess={() => {
            // navigate("/transactions/invoice/0");
          }}
          onCloseModal={() => setEscalateToMasterOpen(false)}
        />
      </Modal>
      <Modal isOpen={rejectOpen} isHeader={false} onCloseModal={() => setRejectOpen(false)} title="Reject Invoice">
        <InvoiceReject
          invoiceData={invoiceDetails}
          onCloseModal={() => setRejectOpen(false)}
          onSuccess={() => navigate("/transactions/invoice/0")}
        />
      </Modal>
      <SelectCounterModal
        isOpen={isCounterModalOpen}
        onClose={closeCounterModal}
        counterList={counterList}
        tempSelectedCounter={tempSelectedCounter}
        setTempSelectedCounter={setTempSelectedCounter}
        onConfirm={confirmCounterSelection}
        allowCustomerNameEntry={allowCustomerNameEntry}
      />
      {/* M10-16162 */}
      <VerifyPoRefModal
        isOpen={poRefVerification.isOpen}
        poRefNumber={poRefNumber}
        matches={poRefVerification.matches}
        onClose={poRefVerification.close}
        onSelectMatch={(po) => {
          handlePoRefChange(po);
          poRefVerification.close();
          toast({ description: `PO Ref No set to ${po?.poid_ref_number}. Please click Update Invoice Header again.` });
        }}
        onConfirm={() => {
          poRefVerification.markVerified();
          handleSubmitInvoiceHeader();
        }}
      />
      {/* M10-16162 */}
      {/* M10-16202 */}
      <Modal isOpen={isEwayModalOpen} isHeader={false} onCloseModal={() => setIsEwayModalOpen(false)} title="Eway Bill">
        <EwayDetails
          invoiceData={invoiceDetails}
          onCloseModal={() => setIsEwayModalOpen(false)}
          onSuccess={() => setIsEwayVerified(true)}
        />
      </Modal>
    </div>
  );
};

export default InvoiceHeader;
